import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// St. Kitts center (iOS DriverMapView default region)
export const ST_KITTS_CENTER = [17.3034, -62.7330];
export const ST_KITTS_ZOOM = 12;

// Esri World Street Map — clean street basemap without OSM's protected-area
// boundaries, marine channels and ferry-route overlays. Free, no API key.
const ESRI_STREETS = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}';
const TILE_LAYERS = {
  standard: {
    url: ESRI_STREETS,
    attribution: 'Tiles &copy; Esri &mdash; Esri, HERE, Garmin, OpenStreetMap contributors',
  },
  // Dark mode: same tiles — the .map-dark CSS filter inverts them.
  // (Free dark tile hosts now require API keys; a CSS filter needs none.)
  dark: {
    url: ESRI_STREETS,
    attribution: 'Tiles &copy; Esri &mdash; Esri, HERE, Garmin, OpenStreetMap contributors',
  },
  // Esri World Imagery — free satellite tiles (replaces MapKit satellite mode)
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
  },
};

function pinIcon(color, emoji = '') {
  return L.divIcon({
    className: '',
    html: `<div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-50%)">
      <div style="width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2.5px solid white;box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center">
        <span style="transform:rotate(45deg);font-size:15px;line-height:1">${emoji}</span>
      </div>
    </div>`,
    iconSize: [34, 40],
    iconAnchor: [17, 40],
  });
}

export const PIN = {
  restaurant: () => pinIcon('#FF6B35', '🍽️'),
  customer: () => pinIcon('#22C55E', '🏠'),
  driver: () => pinIcon('#3B82F6', '🚗'),
  default: () => pinIcon('#FF3B30', '📍'),
};

// Generic Leaflet map wrapper.
// markers: [{ lat, lng, kind: 'restaurant'|'customer'|'driver'|'default', popup }]
// route: [{lat,lng}, ...] polyline
export default function MapView({
  center = ST_KITTS_CENTER,
  zoom = ST_KITTS_ZOOM,
  markers = [],
  route = null,
  mode = 'standard', // 'standard' | 'dark' | 'satellite'
  onReady = null,
  className = '',
  style = {},
}) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const markersRef = useRef([]);
  const routeRef = useRef(null);

  const tileFor = (m) => TILE_LAYERS[m] || TILE_LAYERS.standard;

  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const map = L.map(elRef.current, {
      center, zoom, zoomControl: false,
      attributionControl: true,
    });
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    const t = tileFor(mode);
    layerRef.current = L.tileLayer(t.url, {
      attribution: t.attribution, subdomains: t.subdomains || 'abc', maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    onReady && onReady(map);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (layerRef.current) map.removeLayer(layerRef.current);
    const t = tileFor(mode);
    layerRef.current = L.tileLayer(t.url, {
      attribution: t.attribution, subdomains: t.subdomains || 'abc', maxZoom: 19,
    }).addTo(map);
  }, [mode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = markers
      .filter(m => m.lat != null && m.lng != null)
      .map(m => {
        const marker = L.marker([m.lat, m.lng], { icon: (PIN[m.kind] || PIN.default)() }).addTo(map);
        if (m.popup) marker.bindPopup(m.popup);
        return marker;
      });
    if (markers.length > 1) {
      const bounds = L.latLngBounds(markers.filter(m => m.lat != null).map(m => [m.lat, m.lng]));
      map.fitBounds(bounds.pad(0.25));
    }
  }, [JSON.stringify(markers)]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (routeRef.current) { map.removeLayer(routeRef.current); routeRef.current = null; }
    if (route && route.length > 1) {
      routeRef.current = L.polyline(route.map(p => [p.lat, p.lng]), {
        color: '#3B82F6', weight: 4, opacity: 0.8, dashArray: null,
      }).addTo(map);
    }
  }, [JSON.stringify(route)]);

  return (
    <div
      ref={elRef}
      className={`w-full h-full ${mode === 'dark' ? 'map-dark' : ''} ${className}`}
      style={{ minHeight: 200, ...style }}
    />
  );
}
